import { formatDate, formatGender, formatPhoneNumber, getAge } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { EnrollmentCheckDate, EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { InformationIcon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { AdditionalBenefitInfoDialog } from 'components/referrals/dialogs/AdditionalBenefitInfoDialog';
import {
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';

// eslint-disable-next-line complexity
export const MemberDetails = ({ referralDetails = undefined }) => {
    const { agoraEnhancedEligibility } = useFlags();
    const [isAdditionalBenefitInfoDialogOpen, setIsAdditionalBenefitInfoDialogOpen] =
        useState(false);

    const { memberDetails, isFetching, willBeUnmanagedOn } = useScopeAssessorMemberDetails(
        referralDetails,
        { computeWillBeUnmanagedOn: true },
    );

    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;
    const showWillBeUnmanagedOn =
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING && !!willBeUnmanagedOn;

    return (
        <FormSectionWithRows
            sectionTitle="Member Details"
            width="100%"
            isLoading={isFetching}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Gender"
                        value={formatGender(memberDetails?.gender)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="DOB"
                        value={formatDate(memberDetails?.dateOfBirth)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Age"
                        value={getAge(memberDetails?.dateOfBirth)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Diagnosis"
                        value={referralDetails?.diagnosisDisplayName}
                    />
                    <AddressDetailLabelValue
                        style={{ width: '33%' }}
                        address1={memberDetails?.memberAddress1}
                        address2={memberDetails?.memberAddress2}
                        city={memberDetails?.memberCity}
                        state={memberDetails?.memberState}
                        zip={memberDetails?.memberZip}
                    />
                    <DetailLabelList
                        label="Phone"
                        style={{ width: '34%' }}
                        values={[
                            formatPhoneNumber(memberDetails?.phone),
                            ...(memberDetails?.secondaryPhone
                                ? [
                                      // eslint-disable-next-line react/jsx-key
                                      <div
                                          className="flex__gap--x-small"
                                          style={{ alignItems: 'center' }}
                                      >
                                          <span>
                                              {formatPhoneNumber(memberDetails.secondaryPhone)}
                                          </span>
                                          <InformationIcon tooltipText="Payer-submitted supplementary phone number" />
                                      </div>,
                                  ]
                                : []),
                        ]}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Email"
                        value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Member Id"
                        value={memberDetails?.memberId}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Group Name"
                        value={memberDetails?.benefitGroupName}
                    />
                </>
            }
            rowFourContent={
                <>
                    <AdditionalBenefitInfoDialog
                        payerId={referralDetails?.payerId}
                        benefitGroup={memberDetails?.benefitGroup}
                        memberDetails={memberDetails}
                        isOpen={isAdditionalBenefitInfoDialogOpen}
                        onClose={() => setIsAdditionalBenefitInfoDialogOpen(false)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Carrier"
                        value={memberDetails?.carrier}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Account"
                        value={memberDetails?.account}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Group Id"
                        value={
                            memberDetails?.benefitGroup ? (
                                <ActionButtonText
                                    label={memberDetails.benefitGroup}
                                    onClick={() => setIsAdditionalBenefitInfoDialogOpen(true)}
                                />
                            ) : undefined
                        }
                    />
                </>
            }
            rowFiveContent={
                agoraEnhancedEligibility && eligibilityDisplay ? (
                    <>
                        <div style={{ width: '33%' }}>
                            <DetailLabel label="Enrollment Status" />
                            <StatusIndicator
                                message={eligibilityDisplay.label}
                                status={eligibilityDisplay.status}
                                tooltipText={eligibilityDisplay.tooltipText}
                            />
                        </div>
                        {showWillBeUnmanagedOn ? (
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Grace period ends"
                                value={formatDate(willBeUnmanagedOn)}
                            />
                        ) : null}
                    </>
                ) : undefined
            }
            rowSixContent={
                agoraEnhancedEligibility && eligibilityDisplay ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '33%' }}
                            label="Coverage Dates"
                            value={
                                <EnrollmentCoverageDates
                                    enrollmentStart={memberDetails?.enrollmentStart}
                                    enrollmentEnd={memberDetails?.enrollmentEnd}
                                    isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                                />
                            }
                        />
                        <DetailLabelValuePair
                            style={{ width: '33%' }}
                            label="Enrollment Check Date"
                            value={
                                <EnrollmentCheckDate
                                    enrollmentCheckDate={memberDetails?.enrollmentCheckDate}
                                />
                            }
                        />
                    </>
                ) : undefined
            }
        />
    );
};

MemberDetails.propTypes = {
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string,
        payerId: PropTypes.string,
        diagnosisDisplayName: PropTypes.string,
        segmentId: PropTypes.string,
        memberId: PropTypes.string,
        patientFirstName: PropTypes.string,
        patientLastName: PropTypes.string,
        patientDateOfBirth: PropTypes.string,
    }),
};
